'use client';
import { Box, Divider, Typography } from '@mui/material';

import { ComponentErrorBoundary } from '@/components';
import { useT } from '@/hooks';
import { DayEarnings, ProcessedEarningEvent } from '@/types/earnings.types';
import { groupEventsByType } from '@/utils/earnings.utils';
import { EarningCard } from './EarningCard';
import { NoEarnings } from './NoEarnings';

interface MobileEarningsProps {
  days: DayEarnings[];
}

export const MobileEarnings = ({ days }: MobileEarningsProps) => {
  const { t } = useT();
  if (!days || !Array.isArray(days)) {
    return null;
  }

  const renderEvent = (event: ProcessedEarningEvent, eventIndex: number) => (
    <EarningCard
      key={`${event.ticker || 'unknown'}-${eventIndex}`}
      event={event}
      variant="mobile"
    />
  );

  const renderEventGroup = ([type, events]: [string, ProcessedEarningEvent[]]) => {
    if (!events || !Array.isArray(events) || events.length === 0) return null;
    const renderedEvents = events.map(renderEvent);

    return (
      <Box key={type} sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            fontWeight: 500,
            mb: 1.5,
          }}
        >
          {type}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>{renderedEvents}</Box>
      </Box>
    );
  };

  const renderDay = (day: DayEarnings, dayIndex: number) => {
    if (!day) return null;

    const events = day.events || [];
    const hasEvents = events.length > 0;
    const groupedEvents = hasEvents ? groupEventsByType(events) : {};

    const renderedGroups = Object.entries(groupedEvents)
      .sort(([keyA], [keyB]) => {
        if (keyA === keyB) return 0;
        return keyA === 'Pre Market' ? -1 : 1;
      })
      .map(renderEventGroup);

    return (
      <ComponentErrorBoundary
        key={`${day.day || 'unknown'}-${day.date || 0}`}
        componentName={`Mobile Day - ${day.day || 'Unknown'} ${day.date || ''}`}
        fallback={
          <Box sx={{ p: 2, color: 'error.main' }}>
            {t('earnings.errorLoadingDay', { day: day.day || t('earnings.day') })}
          </Box>
        }
      >
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: day.isToday ? 'primary.main' : 'text.secondary',
              }}
            >
              {day.day || ''} {day.date || ''}
            </Typography>
          </Box>

          {!hasEvents && <NoEarnings variant="mobile" />}

          {hasEvents && <Box sx={{ mb: 3 }}>{renderedGroups}</Box>}

          {dayIndex < days.length - 1 && <Divider sx={{ my: 3 }} />}
        </Box>
      </ComponentErrorBoundary>
    );
  };

  const renderedDays = days.map(renderDay);

  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>{renderedDays}</Box>;
};
